// Multi-step Complaint Wizard
// Step 1: Describe the issue (textarea + optional linked entity hints)
// Step 2: AI triage shows category, severity, suggested KB articles
// Step 3: Choose path (self-help OR escalate)
//   - Self-help: shows the chosen article body. Customer confirms "this helped".
//   - Escalate: optional additional notes + attachments, submit to create ticket.
// Step 4: Confirmation (either "resolved via KB" or "ticket created").
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    ArrowLeft, ArrowRight, Loader2, Sparkles, BookOpen, AlertTriangle, ShieldAlert,
    Headphones, CheckCircle2, ThumbsUp, ChevronRight, FileText, Send, Paperclip, X,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";

const SEVERITY_TONE = {
    low: "border-slate-200 bg-slate-50 text-slate-700",
    medium: "border-amber-200 bg-amber-50 text-amber-800",
    high: "border-orange-200 bg-orange-50 text-orange-800",
    critical: "border-rose-300 bg-rose-50 text-rose-800",
};

const SEVERITY_ICON = {
    low: BookOpen,
    medium: AlertTriangle,
    high: AlertTriangle,
    critical: ShieldAlert,
};

const Stepper = ({ step }) => {
    const steps = ["Describe", "AI Triage", "Resolve", "Done"];
    return (
        <ol className="mb-8 flex items-center justify-between gap-2" data-testid="wizard-stepper">
            {steps.map((label, i) => {
                const idx = i + 1;
                const active = step === idx;
                const done = step > idx;
                return (
                    <li key={label} className="flex flex-1 items-center gap-2">
                        <span
                            className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-semibold ${
                                done ? "bg-emerald-500 text-white" : active ? "bg-brand-700 text-white" : "bg-zinc-200 text-zinc-500"
                            }`}
                        >
                            {done ? <CheckCircle2 className="h-4 w-4" /> : idx}
                        </span>
                        <span className={`hidden text-xs font-medium sm:inline ${active ? "text-zinc-900" : "text-zinc-500"}`}>{label}</span>
                        {idx < steps.length && <span className={`mx-1 h-px flex-1 ${done ? "bg-emerald-300" : "bg-zinc-200"}`} />}
                    </li>
                );
            })}
        </ol>
    );
};

const ComplaintWizardContent = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const initialOrderId = params.get("order_id") || "";

    const [step, setStep] = useState(1);
    const [description, setDescription] = useState("");
    const [orderId, setOrderId] = useState(initialOrderId);
    const [orders, setOrders] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    // Triage result
    const [sessionId, setSessionId] = useState(null);
    const [triage, setTriage] = useState(null);
    const [articles, setArticles] = useState([]);

    // Resolve choice
    const [selectedKbSlug, setSelectedKbSlug] = useState(null);
    const [openArticle, setOpenArticle] = useState(null);
    const [additionalNotes, setAdditionalNotes] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);

    // Final outcome
    const [outcome, setOutcome] = useState(null); // { mode: "self_resolved" | "escalated", kb_title?, ticket_no?, ticket_id?, priority? }

    // Lightweight orders fetch (for linking)
    useEffect(() => {
        let m = true;
        (async () => {
            try {
                const { data } = await api.get("/orders");
                if (m) setOrders((data?.items || data || []).slice(0, 20));
            } catch { /* non-blocking */ }
        })();
        return () => { m = false; };
    }, []);

    const linkedEntities = useMemo(() => (orderId ? { order_id: orderId } : {}), [orderId]);

    const runTriage = async () => {
        const text = description.trim();
        if (text.length < 10) {
            toast.error("Please describe the issue in a bit more detail (at least 10 characters).");
            return;
        }
        try {
            setSubmitting(true);
            const { data } = await api.post("/complaints/triage", {
                description: text,
                linked_entities: linkedEntities,
            });
            setSessionId(data.session_id);
            setTriage(data.triage);
            setArticles(data.suggested_articles || []);
            setSelectedKbSlug(data.suggested_articles?.[0]?.slug || null);
            setStep(2);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "AI triage failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const openArticleDetail = async (slug) => {
        const cached = articles.find((a) => a.slug === slug);
        if (cached && cached.body) {
            setOpenArticle(cached);
            return;
        }
        try {
            const { data } = await api.get(`/kb-articles/${slug}`);
            setOpenArticle(data);
        } catch {
            toast.error("Could not load article");
        }
    };

    const resolveViaSelfHelp = async () => {
        if (!selectedKbSlug || !sessionId) return;
        try {
            setSubmitting(true);
            const { data } = await api.post("/complaints/resolved-self-help", {
                session_id: sessionId,
                kb_slug: selectedKbSlug,
            });
            setOutcome({ mode: "self_resolved", kb_title: data.kb_title, kb_slug: data.kb_slug });
            setStep(4);
            toast.success("Glad we could help!");
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not record resolution");
        } finally {
            setSubmitting(false);
        }
    };

    const onUploadAttachment = async (file) => {
        if (!file) return;
        try {
            setUploading(true);
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/support/upload-attachment", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setAttachments((arr) => [...arr, data.path]);
            toast.success("Attached");
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const escalate = async () => {
        if (!sessionId) return;
        try {
            setSubmitting(true);
            const { data } = await api.post("/complaints/escalate", {
                session_id: sessionId,
                description,
                linked_entities: linkedEntities,
                additional_notes: additionalNotes.trim() || undefined,
                attachments,
            });
            setOutcome({
                mode: "escalated",
                ticket_id: data.ticket_id,
                ticket_no: data.ticket_no,
                priority: data.priority,
                sla_due_at: data.sla_due_at,
            });
            setStep(4);
            toast.success(`Complaint #${data.ticket_no} created — we'll respond soon.`);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not escalate. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="complaint-wizard">
            <Stepper step={step} />

            {/* Step 1 — Describe */}
            {step === 1 && (
                <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft sm:p-8" data-testid="wizard-step-1">
                    <div className="mb-4 flex items-center gap-3">
                        <div className="grid h-10 w-10 place-items-center rounded-xl bg-brand-50 text-brand-700">
                            <Sparkles className="h-5 w-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-zinc-900">Tell us what happened</h2>
                            <p className="text-sm text-zinc-500">Be as specific as you can. Our AI will triage instantly.</p>
                        </div>
                    </div>

                    <label className="mt-3 block text-xs font-semibold uppercase tracking-wider text-zinc-500" htmlFor="cw-desc">
                        Describe your issue
                    </label>
                    <textarea
                        id="cw-desc"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={6}
                        maxLength={4000}
                        placeholder="e.g., I received a different medicine than what I ordered — the strip says Atenolol instead of Telmisartan."
                        className="mt-2 w-full resize-none rounded-2xl border border-zinc-200 p-4 text-sm outline-none focus:border-brand-500"
                        data-testid="wizard-description"
                    />
                    <p className="mt-1 text-right text-[10px] text-zinc-400">{description.length}/4000</p>

                    {orders.length > 0 && (
                        <div className="mt-4">
                            <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500" htmlFor="cw-order">
                                Link an order (optional)
                            </label>
                            <select
                                id="cw-order"
                                value={orderId}
                                onChange={(e) => setOrderId(e.target.value)}
                                className="mt-2 w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-500"
                                data-testid="wizard-link-order"
                            >
                                <option value="">— None —</option>
                                {orders.map((o) => (
                                    <option key={o.id} value={o.id}>
                                        {o.order_no} · {o.status} · ₹{(o.amounts?.total ?? o.totals?.grand_total ?? 0).toFixed?.(0) || ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end">
                        <button
                            onClick={runTriage}
                            disabled={description.trim().length < 10 || submitting}
                            className="btn-brand"
                            data-testid="wizard-run-triage"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            {submitting ? "Analyzing…" : "Get AI triage"}
                        </button>
                    </div>
                </div>
            )}

            {/* Step 2 — AI Triage result */}
            {step === 2 && triage && (
                <div className="space-y-4" data-testid="wizard-step-2">
                    <div className="rounded-3xl border border-brand-200 bg-gradient-to-br from-brand-50/60 to-violet-50/40 p-6 shadow-soft sm:p-8">
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-brand-700 ring-1 ring-brand-200">
                                <Sparkles className="h-3.5 w-3.5" /> AI triage
                            </span>
                            <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${SEVERITY_TONE[triage.severity] || SEVERITY_TONE.medium}`}>
                                {triage.severity} severity
                            </span>
                            <span className="rounded-full bg-white px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-700 ring-1 ring-zinc-200">
                                {(triage.subtype || "other").replace(/_/g, " ")}
                            </span>
                            {triage._fallback && (
                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">heuristic fallback</span>
                            )}
                        </div>
                        <p className="text-sm leading-relaxed text-zinc-800" data-testid="wizard-ai-response">
                            {triage.customer_facing_response || triage.root_cause_hypothesis}
                        </p>
                        {triage.root_cause_hypothesis && triage.customer_facing_response && (
                            <p className="mt-3 text-xs italic text-zinc-500">
                                <span className="font-semibold">Likely cause:</span> {triage.root_cause_hypothesis}
                            </p>
                        )}
                    </div>

                    {articles.length > 0 ? (
                        <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft sm:p-8">
                            <h3 className="text-base font-semibold text-zinc-900">Try one of these first</h3>
                            <p className="mt-1 text-sm text-zinc-500">These articles match your issue.</p>
                            <div className="mt-4 space-y-2" data-testid="wizard-kb-suggestions">
                                {articles.map((a) => (
                                    <button
                                        key={a.slug}
                                        onClick={() => openArticleDetail(a.slug)}
                                        className="group flex w-full items-start gap-3 rounded-2xl border border-zinc-100 bg-zinc-50/40 p-4 text-left transition hover:border-brand-300 hover:bg-brand-50/40"
                                        data-testid={`wizard-kb-${a.slug}`}
                                    >
                                        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-100 text-brand-700">
                                            <BookOpen className="h-4 w-4" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-zinc-900 line-clamp-1">{a.title}</p>
                                            {a.why_relevant && (
                                                <p className="mt-0.5 text-xs italic text-zinc-500 line-clamp-2">{a.why_relevant}</p>
                                            )}
                                        </div>
                                        <ChevronRight className="h-5 w-5 shrink-0 text-zinc-400 transition group-hover:translate-x-1" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-3xl border border-zinc-100 bg-white p-6 text-sm text-zinc-600">
                            We couldn&apos;t find a self-help article for this issue. Please escalate so our team can help.
                        </div>
                    )}

                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
                        <button
                            onClick={() => setStep(1)}
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-zinc-200 px-5 py-2.5 text-sm font-medium text-zinc-700 hover:border-zinc-300"
                            data-testid="wizard-back-1"
                        >
                            <ArrowLeft className="h-4 w-4" /> Edit description
                        </button>
                        <button
                            onClick={() => setStep(3)}
                            className="btn-brand"
                            data-testid="wizard-continue-3"
                        >
                            I need more help <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Inline article viewer modal */}
                    {openArticle && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-4" data-testid="wizard-article-modal">
                            <div className="max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
                                <header className="flex items-center justify-between border-b border-zinc-100 px-6 py-4">
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-600">Self-help</p>
                                        <h4 className="text-base font-semibold text-zinc-900">{openArticle.title}</h4>
                                    </div>
                                    <button onClick={() => setOpenArticle(null)} className="rounded-full p-2 text-zinc-400 hover:bg-zinc-100" aria-label="Close" data-testid="wizard-article-close">
                                        <X className="h-4 w-4" />
                                    </button>
                                </header>
                                <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
                                    <div className="prose prose-sm max-w-none prose-zinc">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{openArticle.body || ""}</ReactMarkdown>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between border-t border-zinc-100 px-6 py-4">
                                    <button onClick={() => setOpenArticle(null)} className="text-sm font-medium text-zinc-600 hover:text-zinc-900">
                                        Keep reading
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedKbSlug(openArticle.slug);
                                            setOpenArticle(null);
                                            resolveViaSelfHelp();
                                        }}
                                        className="btn-brand"
                                        data-testid="wizard-article-resolved"
                                    >
                                        <ThumbsUp className="h-4 w-4" /> This solved my issue
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Step 3 — Resolve choice */}
            {step === 3 && triage && (
                <div className="space-y-4" data-testid="wizard-step-3">
                    <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft sm:p-8">
                        <div className="mb-3 flex items-center gap-3">
                            <div className="grid h-10 w-10 place-items-center rounded-xl bg-rose-50 text-rose-700">
                                <Headphones className="h-5 w-5" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-zinc-900">Talk to a human</h2>
                                <p className="text-sm text-zinc-500">We&apos;ll create a complaint ticket and our team will respond within the SLA.</p>
                            </div>
                        </div>

                        <label className="mt-3 block text-xs font-semibold uppercase tracking-wider text-zinc-500" htmlFor="cw-notes">
                            Anything else our team should know? (optional)
                        </label>
                        <textarea
                            id="cw-notes"
                            value={additionalNotes}
                            onChange={(e) => setAdditionalNotes(e.target.value)}
                            rows={3}
                            maxLength={2000}
                            placeholder="Additional context, order/batch numbers, screenshots…"
                            className="mt-2 w-full resize-none rounded-2xl border border-zinc-200 p-4 text-sm outline-none focus:border-brand-500"
                            data-testid="wizard-additional-notes"
                        />

                        {/* Attachments */}
                        <div className="mt-4">
                            <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500">Attachments (optional)</label>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                {attachments.map((p) => (
                                    <span key={p} className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-3 py-1 text-[11px] text-zinc-700">
                                        <FileText className="h-3 w-3" /> {p.split("/").pop()}
                                        <button
                                            onClick={() => setAttachments((arr) => arr.filter((x) => x !== p))}
                                            className="ml-1 text-zinc-400 hover:text-rose-600"
                                            aria-label="Remove attachment"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-dashed border-zinc-300 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:border-brand-400 hover:text-brand-700">
                                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
                                    {uploading ? "Uploading…" : "Add file (jpg/png/pdf · ≤5MB)"}
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/jpg,application/pdf"
                                        className="hidden"
                                        onChange={(e) => onUploadAttachment(e.target.files?.[0])}
                                        data-testid="wizard-attachment-input"
                                    />
                                </label>
                            </div>
                        </div>

                        {/* Triage callout */}
                        <div className="mt-4 rounded-2xl bg-zinc-50 p-4 text-xs text-zinc-600">
                            Your complaint will be tagged as <strong className="text-zinc-900">{(triage.subtype || "other").replace(/_/g, " ")}</strong>{" "}
                            with severity <strong className="text-zinc-900">{triage.severity}</strong>.
                            {triage.requires_callback && <> A callback is recommended.</>}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
                        <button
                            onClick={() => setStep(2)}
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-zinc-200 px-5 py-2.5 text-sm font-medium text-zinc-700 hover:border-zinc-300"
                            data-testid="wizard-back-2"
                        >
                            <ArrowLeft className="h-4 w-4" /> Back to suggestions
                        </button>
                        <button
                            onClick={escalate}
                            disabled={submitting}
                            className="btn-brand"
                            data-testid="wizard-escalate"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            {submitting ? "Submitting…" : "Submit complaint"}
                        </button>
                    </div>
                </div>
            )}

            {/* Step 4 — Confirmation */}
            {step === 4 && outcome && (
                <div className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-8 shadow-soft text-center" data-testid="wizard-step-4">
                    <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-emerald-500 text-white shadow-lg">
                        <CheckCircle2 className="h-7 w-7" />
                    </div>
                    {outcome.mode === "self_resolved" ? (
                        <>
                            <h2 className="text-2xl font-semibold text-zinc-900">Glad we could help!</h2>
                            <p className="mt-2 text-sm text-zinc-600">
                                You marked <span className="font-semibold">&ldquo;{outcome.kb_title}&rdquo;</span> as the resolution.
                            </p>
                            <div className="mt-6 flex flex-col items-center justify-center gap-2 sm:flex-row">
                                <Link to="/help/kb" className="rounded-full border border-zinc-200 px-5 py-2.5 text-sm font-medium hover:border-brand-300" data-testid="wizard-browse-kb">
                                    Browse more articles
                                </Link>
                                <Link to="/" className="btn-brand" data-testid="wizard-done-home">
                                    Back to home <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </>
                    ) : (
                        <>
                            <h2 className="text-2xl font-semibold text-zinc-900">Complaint submitted</h2>
                            <p className="mt-2 text-sm text-zinc-600">
                                We&apos;ve created <span className="font-mono font-semibold">{outcome.ticket_no}</span> with{" "}
                                <span className="font-semibold">{outcome.priority}</span> priority.
                            </p>
                            {outcome.sla_due_at && (
                                <p className="mt-1 text-xs text-zinc-500">
                                    SLA: respond by {new Date(outcome.sla_due_at).toLocaleString()}
                                </p>
                            )}
                            <div className="mt-6 flex flex-col items-center justify-center gap-2 sm:flex-row">
                                <Link to="/complaints" className="rounded-full border border-zinc-200 px-5 py-2.5 text-sm font-medium hover:border-brand-300" data-testid="wizard-list-link">
                                    View my complaints
                                </Link>
                                <Link to={`/complaints/${outcome.ticket_id}`} className="btn-brand" data-testid="wizard-view-complaint">
                                    Open complaint <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Self-help inline (visible alongside step 2 once an article is selected from list) */}
            {step === 2 && selectedKbSlug && !openArticle && (
                <div className="mt-6 flex justify-center" data-testid="wizard-resolve-action">
                    <button
                        onClick={resolveViaSelfHelp}
                        disabled={submitting}
                        className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-5 py-2.5 text-sm font-semibold text-emerald-800 transition hover:border-emerald-400 hover:bg-emerald-100 disabled:opacity-60"
                        data-testid="wizard-mark-resolved"
                    >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ThumbsUp className="h-4 w-4" />}
                        Mark the first article as solved
                    </button>
                </div>
            )}
        </div>
    );
};

const ComplaintWizard = () => (
    <Layout>
        <section className="mx-auto max-w-3xl px-6 py-10 lg:px-12">
            <Link to="/complaints" className="mb-3 inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-brand-700" data-testid="wizard-back-link">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to complaints
            </Link>
            <header className="mb-8">
                <p className="label-uppercase">AI-guided</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">Raise a complaint</h1>
                <p className="mt-1 text-sm text-zinc-500">
                    Describe what happened — our AI will categorize, suggest fixes, and route to a human if needed.
                </p>
            </header>
            <AuthGuard>
                <ComplaintWizardContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default ComplaintWizard;
